<template>
  <div>
    <ValidationObserver ref="observer">
      <CRow>
        <CCol col="12" md="6">
          <CCard>
            <CCardHeader>
              <circle-icon></circle-icon>
              농장 기본정보
            </CCardHeader>
            <CCardBody>
              <ValidationProvider v-slot="{ errors }" name="cropCode" rules="required">
                <CSelect
                  label="작물"
                  placeholder="작물를 선택하세요"
                  horizontal
                  :value.sync="form.cropCode"
                  :options="cropCode.selectList"
                  id="code"
                  :invalid-feedback="errors.length > 0 ? errors[0] : ''"
                  :is-valid="errors.length > 0 ? false : null"
                >
                </CSelect>
              </ValidationProvider>

              <ValidationProvider v-slot="{ errors }" name="address" rules="required|min:1|max:100">
                <div role="group" class="form-group form-row">
                  <label class="col-form-label col-sm-3"> 수집지역 </label>
                  <div class="col-sm-9 input-group">
                    <input
                      type="text"
                      readonly="readonly"
                      placeholder="수집지역을 검색하세요"
                      class="form-control bg-color-white"
                      v-model="view.address"
                      :class="errors.length > 0 ? 'is-invalid' : ''"
                    />
                    <div class="input-group-append">
                      <CButton color="primary" @click="showSearchAddressModal">
                        <i class="flaticon flaticon-location" aria-hidden="true"></i>
                      </CButton>
                    </div>
                    <div class="invalid-feedback" v-if="errors.length > 0">
                      {{ errors[0] }}
                    </div>
                  </div>
                </div>
              </ValidationProvider>

              <CInput
                label="농장주"
                placeholder="농장주를 입력하세요"
                v-model="form.farmerName"
                horizontal
              ></CInput>

              <ValidationProvider v-slot="{ errors }" name="kindCode" rules="required">
                <CSelect
                  label="온실형태"
                  placeholder="온실형태를 선택하세요"
                  horizontal
                  :value.sync="form.kindCode"
                  :options="kindCodeList"
                  :invalid-feedback="errors.length > 0 ? errors[0] : ''"
                  :is-valid="errors.length > 0 ? false : null"
                >
                </CSelect>
              </ValidationProvider>
            </CCardBody>
          </CCard>
        </CCol>

        <CCol col="12" md="6">
          <CCard>
            <CCardHeader>
              <circle-icon></circle-icon>
              온실 기상환경
            </CCardHeader>
            <CCardBody>
              <v-date-picker
                v-model="form.date"
                :max-date="getMaxDate()"
                :masks="{ title: 'YYYY년 MMM', L: 'YYYY-MM-DD' }"
                class="w-100"
              >
                <template v-slot="{ inputValue, togglePopover }">
                  <div role="group" class="form-group form-row">
                    <label class="col-form-label col-sm-3"> 날짜 </label>
                    <div class="col-sm-9 input-group">
                      <input
                        type="text"
                        readonly="readonly"
                        placeholder="날짜를 선택하세요"
                        class="form-control bg-color-white"
                        :value="inputValue"
                        @click="togglePopover({ placement: 'bottom-end' })"
                      />
                    </div>
                  </div>
                </template>
              </v-date-picker>
              <div role="group" class="form-group form-row">
                <label class="col-form-label col-sm-3"> 온도 </label>
                <div class="col-sm-9 input-group">
                  <input
                    type="number"
                    placeholder="온도를 입력하세요"
                    class="form-control bg-color-white"
                    v-model="form.temp"
                  />
                  <div class="input-group-append">
                    <span class="input-group-text">℃</span>
                  </div>
                </div>
              </div>
              <div role="group" class="form-group form-row">
                <label class="col-form-label col-sm-3"> 습도 </label>
                <div class="col-sm-9 input-group">
                  <input
                    type="number"
                    placeholder="습도를 입력하세요"
                    class="form-control bg-color-white"
                    v-model="form.humidity"
                  />
                  <div class="input-group-append">
                    <span class="input-group-text">%</span>
                  </div>
                </div>
              </div>
              <div class="d-none d-md-block" style="height: 50px"></div>
            </CCardBody>
          </CCard>
        </CCol>

        <CCol col="12">
          <CCard>
            <CCardHeader>
              <circle-icon></circle-icon>
              병해 수집정보
            </CCardHeader>
            <CCardBody>
              <CRow>
                <CCol col="12" md="4">
                  <vue-upload-multiple-image
                    @upload-success="uploadImageSuccess"
                    @before-remove="beforeRemove"
                    @edit-image="editImage"
                    @mark-is-primary="changedImageFileList"
                    @limit-exceeded="limitExceeded"
                    class="w-100"
                    :data-images="imageList"
                    :max-image="maxImageSize"
                  ></vue-upload-multiple-image>
                  <ValidationProvider v-slot="{ errors }" name="image" rules="required|min:1">
                    <div class="input-group">
                      <input
                        type="hidden"
                        v-model="uploadImages"
                        :class="errors.length > 0 ? 'is-invalid' : ''"
                      />
                      <div class="invalid-feedback" v-if="errors.length > 0">
                        {{ errors[0] }}
                      </div>
                    </div>
                  </ValidationProvider>
                </CCol>
                <CCol col="12" md="4" class="mt-4 mt-md-0">
                  <ValidationProvider
                    v-slot="{ errors }"
                    name="diseaseName"
                    rules="required|min:1|max:100"
                  >
                    <CInput
                      label="병해이름"
                      placeholder="병해 이름을 입력하세요"
                      v-model="form.diseaseName"
                      :invalid-feedback="errors.length > 0 ? errors[0] : ''"
                      :is-valid="errors.length > 0 ? false : null"
                      horizontal
                    ></CInput>
                  </ValidationProvider>

                  <div role="group" class="form-group" v-if="isSelectedCrop('strawberry')">
                    <label class="col-form-label">작물상 위치</label>
                    <div class="input-group">
                      <img src="/img/disease/strawberry.jpg" style="width: 100%" />

                      <button
                        type="button"
                        class="btn btn-light"
                        :class="getCropPositionClass(1)"
                        style="position: absolute; left: 60%; top: 15%"
                        @click="changeCropPosition(1)"
                      >
                        1. 꽃
                      </button>
                      <button
                        type="button"
                        class="btn btn-light"
                        :class="getCropPositionClass(2)"
                        style="position: absolute; left: 14%; top: 30%"
                        @click="changeCropPosition(2)"
                      >
                        2. 잎
                      </button>
                      <button
                        type="button"
                        class="btn btn-light"
                        :class="getCropPositionClass(3)"
                        style="position: absolute; left: 50%; top: 60%"
                        @click="changeCropPosition(3)"
                      >
                        3. 과실
                      </button>
                      <button
                        type="button"
                        class="btn btn-light"
                        :class="getCropPositionClass(4)"
                        style="position: absolute; left: 14%; top: 10%"
                        @click="changeCropPosition(4)"
                      >
                        4. 뿌리
                      </button>

                      <ValidationProvider v-slot="{ errors }" name="cropPosition" rules="required">
                        <div class="input-group">
                          <input
                            type="hidden"
                            v-model="form.cropPosition"
                            :class="errors.length > 0 ? 'is-invalid' : ''"
                          />
                          <div class="invalid-feedback" v-if="errors.length > 0">
                            {{ errors[0] }}
                          </div>
                        </div>
                      </ValidationProvider>
                    </div>
                  </div>
                  <div role="group" class="form-group" v-else-if="isSelectedCrop('tomato')">
                    <label class="col-form-label">작물상 위치</label>
                    <div class="input-group">
                      <img src="/img/disease/tomato.jpg" style="width: 100%" />

                      <button
                        type="button"
                        class="btn btn-light"
                        :class="getCropPositionClass(1)"
                        style="position: absolute; left: 20z%; top: 50%"
                        @click="changeCropPosition(1)"
                      >
                        1. 꽃
                      </button>
                      <button
                        type="button"
                        class="btn btn-light"
                        :class="getCropPositionClass(2)"
                        style="position: absolute; left: 70%; top: 20%"
                        @click="changeCropPosition(2)"
                      >
                        2. 잎
                      </button>
                      <button
                        type="button"
                        class="btn btn-light"
                        :class="getCropPositionClass(3)"
                        style="position: absolute; left: 50%; top: 60%"
                        @click="changeCropPosition(3)"
                      >
                        3. 과실
                      </button>
                      <button
                        type="button"
                        class="btn btn-light"
                        :class="getCropPositionClass(4)"
                        style="position: absolute; left: 35%; top: 5%"
                        @click="changeCropPosition(4)"
                      >
                        4. 뿌리
                      </button>

                      <ValidationProvider v-slot="{ errors }" name="cropPosition" rules="required">
                        <div class="input-group">
                          <input
                            type="hidden"
                            v-model="form.cropPosition"
                            :class="errors.length > 0 ? 'is-invalid' : ''"
                          />
                          <div class="invalid-feedback" v-if="errors.length > 0">
                            {{ errors[0] }}
                          </div>
                        </div>
                      </ValidationProvider>
                    </div>
                  </div>
                  <div role="group" class="form-group" v-else-if="isSelectedCrop('paprika')">
                    <label class="col-form-label">작물상 위치</label>
                    <div class="input-group">
                      <img src="/img/disease/paprika.jpg" style="width: 100%" />

                      <button
                        type="button"
                        class="btn btn-light"
                        :class="getCropPositionClass(1)"
                        style="position: absolute; left: 60%; top: 50%"
                        @click="changeCropPosition(1)"
                      >
                        1. 꽃
                      </button>
                      <button
                        type="button"
                        class="btn btn-light"
                        :class="getCropPositionClass(2)"
                        style="position: absolute; left: 70%; top: 20%"
                        @click="changeCropPosition(2)"
                      >
                        2. 잎
                      </button>
                      <button
                        type="button"
                        class="btn btn-light"
                        :class="getCropPositionClass(3)"
                        style="position: absolute; left: 50%; top: 60%"
                        @click="changeCropPosition(3)"
                      >
                        3. 과실
                      </button>
                      <button
                        type="button"
                        class="btn btn-light"
                        :class="getCropPositionClass(4)"
                        style="position: absolute; left: 35%; top: 5%"
                        @click="changeCropPosition(4)"
                      >
                        4. 뿌리
                      </button>

                      <ValidationProvider v-slot="{ errors }" name="cropPosition" rules="required">
                        <div class="input-group">
                          <input
                            type="hidden"
                            v-model="form.cropPosition"
                            :class="errors.length > 0 ? 'is-invalid' : ''"
                          />
                          <div class="invalid-feedback" v-if="errors.length > 0">
                            {{ errors[0] }}
                          </div>
                        </div>
                      </ValidationProvider>
                    </div>
                  </div>
                </CCol>
                <CCol col="12" md="4" class="mt-4 mt-md-0">
                  <ValidationProvider v-slot="{ errors }" name="diseaseStep" rules="required">
                    <CSelect
                      label="병해단계"
                      placeholder="병해 단계를 선택하세요"
                      horizontal
                      :value.sync="form.diseaseStep"
                      :options="diseaseStepList"
                      :invalid-feedback="errors.length > 0 ? errors[0] : ''"
                      :is-valid="errors.length > 0 ? false : null"
                    >
                    </CSelect>
                  </ValidationProvider>

                  <div role="group" class="form-group">
                    <label class="col-form-label">온실내 위치</label>
                    <div class="input-group">
                      <img src="/img/disease/house.png" style="width: 100%" />

                      <button
                        type="button"
                        class="btn btn-light scc-rounded"
                        :class="getHousePositionClass(1)"
                        style="position: absolute; left: 10%; top: 35%"
                        @click="changeHousePosition(1)"
                      >
                        1
                      </button>

                      <button
                        type="button"
                        class="btn btn-light scc-rounded"
                        :class="getHousePositionClass(2)"
                        style="position: absolute; left: 23%; top: 50%"
                        @click="changeHousePosition(2)"
                      >
                        2
                      </button>

                      <button
                        type="button"
                        class="btn btn-light scc-rounded"
                        :class="getHousePositionClass(3)"
                        style="position: absolute; left: 36%; top: 65%"
                        @click="changeHousePosition(3)"
                      >
                        3
                      </button>

                      <button
                        type="button"
                        class="btn btn-light scc-rounded"
                        :class="getHousePositionClass(4)"
                        style="position: absolute; left: 35%; top: 25%"
                        @click="changeHousePosition(4)"
                      >
                        4
                      </button>

                      <button
                        type="button"
                        class="btn btn-light scc-rounded"
                        :class="getHousePositionClass(5)"
                        style="position: absolute; left: 48%; top: 40%"
                        @click="changeHousePosition(5)"
                      >
                        5
                      </button>

                      <button
                        type="button"
                        class="btn btn-light scc-rounded"
                        :class="getHousePositionClass(6)"
                        style="position: absolute; left: 61%; top: 55%"
                        @click="changeHousePosition(6)"
                      >
                        6
                      </button>

                      <button
                        type="button"
                        class="btn btn-light scc-rounded"
                        :class="getHousePositionClass(7)"
                        style="position: absolute; left: 60%; top: 15%"
                        @click="changeHousePosition(7)"
                      >
                        7
                      </button>

                      <button
                        type="button"
                        class="btn btn-light scc-rounded"
                        :class="getHousePositionClass(8)"
                        style="position: absolute; left: 73%; top: 30%"
                        @click="changeHousePosition(8)"
                      >
                        8
                      </button>

                      <button
                        type="button"
                        class="btn btn-light scc-rounded"
                        :class="getHousePositionClass(9)"
                        style="position: absolute; left: 86%; top: 45%"
                        @click="changeHousePosition(9)"
                      >
                        9
                      </button>

                      <ValidationProvider v-slot="{ errors }" name="housePosition" rules="required">
                        <div class="input-group">
                          <input
                            type="hidden"
                            v-model="form.housePosition"
                            :class="errors.length > 0 ? 'is-invalid' : ''"
                          />
                          <div class="invalid-feedback" v-if="errors.length > 0">
                            {{ errors[0] }}
                          </div>
                        </div>
                      </ValidationProvider>
                    </div>
                  </div>
                </CCol>
              </CRow>
            </CCardBody>
          </CCard>
        </CCol>
        <CCol col="12" class="text-right mb-4">
          <button type="button" class="btn btn-primary" @click="submit">저장</button>
        </CCol>
      </CRow>
    </ValidationObserver>
    <SearchAddressModal v-if="modal.searchAddress.visible" :properties="modal.searchAddress">
    </SearchAddressModal>
  </div>
</template>

<script>
import core from "@/core";
import SearchAddressModal from "@/modals/core/SearchAddressModal";
import VueUploadMultipleImage from "@/components/VueUploadMultipleImage";
import DiseaseService from "@/services/disease/disease.service";
import CodeService from "@/services/code/code.service";
import { extend, localize, ValidationObserver, ValidationProvider } from "vee-validate";
import { max, min, required } from "vee-validate/dist/rules";
import Constant from "@/store/constant";

extend("required", required);
extend("min", min);
extend("max", max);

localize("ko", {
  messages: Constant.validate.language.korea.messages,
  names: {
    address: "수집지역",
    kindCode: "온실형태",
    diseaseName: "병해이름",
    diseaseStep: "병해단계",
    cropPosition: "작물상 위치",
    housePosition: "온실내 위치",
    image: "이미지",
    cropCode: "작물",
  },
});

export default {
  name: "DiseaseInfoAdd",
  components: {
    SearchAddressModal,
    VueUploadMultipleImage,
    ValidationObserver,
    ValidationProvider,
  },
  data() {
    return {
      type: "",
      diseaseInfo: null,
      updateViewType: null,
      form: {
        cropCode: null,
        farmerName: "",
        address1: "",
        address2: "",
        latitude: null,
        longitude: null,
        kindCode: null,
        date: null,
        temp: null,
        humidity: null,
        diseaseName: null,
        diseaseStep: null,
        cropPosition: null,
        housePosition: null,
      },
      maxImageSize: 20,
      imageList: [],
      uploadImages: [],
      view: {
        address: "",
      },
      modal: {
        searchAddress: {
          visible: false,
          params: {
            type: "add",
            item: null,
          },
          watchClose: {
            result: "",
            item: null,
          },
        },
      },
      kindCodeList: [
        {
          label: "단동",
          value: "S",
        },
        {
          label: "연동",
          value: "M",
        },
      ],
      diseaseStepList: [
        {
          label: "초기",
          value: "1",
        },
        {
          label: "중기",
          value: "2",
        },
        {
          label: "말기",
          value: "3",
        },
      ],
      cropCode: {
        selectList: [],
        list: [],
      },
    };
  },
  mounted() {
    this.$nextTick(async () => {
      if (this.$route.path.indexOf("/add") > -1) {
        this.type = "add";
      } else {
        this.type = "edit";
        let id = Number(this.$route.query.id);
        if (id > 0) {
          try {
            const diseaseInfo = (this.diseaseInfo = await DiseaseService.getDiseaseInfo(id));
            console.log("diseaseInfo : ", diseaseInfo);
            for (const key of Object.keys(this.form)) {
              this.form[key] = diseaseInfo[key];
            }

            if (diseaseInfo.address2 != null) {
              this.view.address = `${diseaseInfo.address1} ${diseaseInfo.address2}`;
            } else {
              this.view.address = `${diseaseInfo.address1}`;
            }

            diseaseInfo.imageList.forEach((image, index) => {
              console.log("image : ", image);
              const isDefault = index === 0;

              this.imageList.push({
                path: image.downloadUri,
                default: isDefault,
                highlight: 1,
                id: image.id,
              });
              this.uploadImages = this.imageList;
            });
            this.updateViewType = "edit";
            console.log("this.form : ", this.form);
          } catch (e) {
            console.log(e);
            id = -1;
          }
        }
        if (id <= 0) {
          await core.alert.show({
            title: "알림",
            body: "잘못된 요청입니다.",
          });
          this.$router.back();
          return;
        }
      }
      const cropCodeList = await CodeService.getCropCodeList();
      cropCodeList.forEach((cropCode) => {
        this.cropCode.selectList.push({ label: cropCode.koName, value: cropCode.code });
      });
      this.cropCode.list = cropCodeList;
      core.loader.hide();
    });
  },
  watch: {
    "modal.searchAddress.watchClose"() {
      const modal = this.modal.searchAddress;
      if (modal.watchClose.result === "confirm") {
        const item = modal.watchClose.item;
        //console.log("item : ", item);
        if (item.address2 != null) {
          this.view.address = `${item.address1} ${item.address2}`;
        } else {
          this.view.address = `${item.address1}`;
        }
        this.form.address1 = item.address1;
        this.form.address2 = item.address2;
        if (this.form.address2 == null) this.form.address2 = "";
        this.form.latitude = item.latitude;
        this.form.longitude = item.longitude;
      }
    },
  },
  // updated() {
  //   if (this.updateViewType === "edit") {
  //     this.updateViewType = null;
  //     console.log("updated");
  //     this.changeCropPosition(this.diseaseInfo.cropPosition);
  //     // this.changeHousePosition(this.diseaseInfo.housePosition);
  //   }
  // },
  methods: {
    isSelectedCrop(crop) {
      let cropCode = null;
      this.cropCode.list.some((_cropCode) => {
        if (_cropCode.enName === crop) {
          cropCode = _cropCode;
          return true;
        }
      });

      if (cropCode != null) {
        return this.form.cropCode === cropCode.code;
      }
      return false;
    },
    changeCropPosition(position) {
      this.form.cropPosition = String(position);
    },
    getCropPositionClass(position) {
      position = String(position);
      if (this.form.cropPosition === position) {
        return "active";
      }
      return "";
    },
    changeHousePosition(position) {
      this.form.housePosition = String(position);
    },
    getHousePositionClass(position) {
      position = String(position);
      if (this.form.housePosition === position) {
        return "active";
      }
      return "";
    },
    getMaxDate() {
      return core.date.instance().add(1, "days").toDate();
    },
    showSearchAddressModal() {
      const modal = this.modal.searchAddress;
      modal.visible = true;
    },
    uploadImageSuccess(index, fileList) {
      this.uploadImages = fileList;
    },
    changedImageFileList(index, fileList) {
      this.uploadImages = fileList;
    },
    beforeRemove(index, done, fileList) {
      console.log("beforeRemove");
      done();
      this.uploadImages = fileList;
    },
    editImage(index, fileList) {
      console.log("edit image", index, fileList);
      this.uploadImages = fileList;
      delete fileList[index].id;
    },
    limitExceeded() {
      console.log("limitExceeded");
      core.alert.show({
        title: "알림",
        body: `최대 업로드 수(${this.maxImageSize})를 초과하였습니다.`,
      });
    },
    async submit() {
      // 값 유효성 검사
      const observer = this.$refs.observer;
      const isValid = await observer.validate();
      if (isValid) {
        // 이미지 업로드
        console.log("this.uploadImages : ", this.uploadImages);
        const imageTotalSize = this.uploadImages.length;
        const imageFileIdList = [];
        let count = 0;
        for (const image of this.uploadImages) {
          try {
            if (image.id != null) {
              imageFileIdList.push(image.id);
            } else {
              const formData = new FormData();
              formData.append("imageFile", image.blob, image.name);
              core.loader.show(`이미지 업로드 ${++count}/${imageTotalSize}`);
              const fileDetailId = await DiseaseService.createDiseaseInfoImage(formData);
              imageFileIdList.push(fileDetailId);
            }
          } catch (e) {
            console.log(e);
            await core.alert.show({
              title: "알림",
              body: "이미지 업로드 실패!",
            });
            return;
          }
        }
        // 병해충 정보 저장
        const params = core.utils.deepCopy(this.form);
        params.imageFileIdList = imageFileIdList;
        console.log("params : ", params);
        try {
          if (this.type === "add") {
            const diseaseInfo = await DiseaseService.createDiseaseInfo(params);
            console.log("create, diseaseInfo : ", diseaseInfo);
          } else if (this.type === "edit") {
            const diseaseInfo = await DiseaseService.updateDiseaseInfo(this.diseaseInfo.id, params);
            console.log("update, diseaseInfo : ", diseaseInfo);
          }
          this.$router.back();
        } catch (e) {
          console.log(e);
        }
        core.loader.hide();
      } else {
        console.log("errors : ", observer.errors);
      }
    },
  },
};
</script>
<style>
.image-container {
  width: 100% !important;
  min-height: 300px;
  height: 80% !important;
}

.preview-image {
  height: 100%;
  min-height: 270px;
}

.show-img {
  max-width: 100% !important;
  max-height: 200px !important;
}

.image-list-container {
  max-width: 100% !important;
}

.btn-light {
  background: rgba(35, 35, 35, 0.4);
  font-weight: bold;
  color: #fff;
}

.btn-light.active {
  background: rgba(35, 35, 35, 1);
  border: 2px solid #38ff00;
}
</style>
